import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { googleLogout } from '@react-oauth/google';
import { useLazyQuery, useMutation } from '@apollo/client';
import PropTypes from 'prop-types';

import getCookie from '../utils/cookie';
import { GET_USER, LOGOUT_USER } from '../apis/user';
import { LOGGED_IN_USER, SHOW_TIMELINE_PROMPT } from '../constants/common';
import useLoader from './LoaderContext';
import useWorkspaceInvite from './WorkspaceInviteContext';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  // init
  const { isInviteUser } = useWorkspaceInvite();
  const { isLoading, setIsLoading } = useLoader();

  // states
  const [authUser, setAuthUser] = useState(undefined);
  const [isLoggedIn, setIsLoggedIn] = useState(!!getCookie(LOGGED_IN_USER));

  // mutations/queries
  const [getUser] = useLazyQuery(GET_USER, { fetchPolicy: 'network-only' });
  const [logOutUser] = useMutation(LOGOUT_USER);

  const logOut = async () => {
    googleLogout();
    await logOutUser();
    localStorage.removeItem(SHOW_TIMELINE_PROMPT);
  };

  const fetchUser = async () => {
    try {
      if (isLoggedIn) {
        if (!isLoading && !isInviteUser) setIsLoading(true);
        const { data: { user: userData } } = await getUser();
        setAuthUser({ ...userData, id: parseInt(userData.id, 10) });
      }
    } catch (authError) {
      setIsLoggedIn(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [isLoggedIn]);

  // constants
  const values = useMemo(() => ({
    authUser,
    isLoggedIn,
    setAuthUser,
    setIsLoggedIn,
    logOut,
  }), [authUser, isLoggedIn]);

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
};

AuthProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default useAuth;
